import { Brain, Syringe, Pill, CalendarDays } from 'lucide-react';

/*
 * MD Anderson Neuroanesthesia rotation. Built from the Blackboard Neuro Orientation
 * (2026 orientation document). NeuRecipes and the scalp block reviews are linked, not
 * transcribed. See docs/blackboard-md-anderson.md.
 */

const BB_DOC = (id) => `https://bcm.blackboard.com/ultra/courses/_129_1/document/${id}?view=content&state=view`;
const NEURO = BB_DOC('_586106_1');

export const mdaNeuro = {
  accent: '#da322a',
  icon: Brain,
  tagline: 'Craniotomy setup, scalp blocks, MDA pharmacy rules',
  notesKey: 'notes:md-anderson:neuro',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'setup', title: 'Case Setup', subtitle: 'The standard crani: lines, TIVA, drips, drugs', icon: Syringe, action: 'guide:setup', featured: true },
      { id: 'scalp', title: 'Scalp Block', subtitle: 'Mix and draw-up', icon: Pill, action: 'guide:scalp' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'day', title: 'Your Day', subtitle: 'Before you start, first day, pre-charting, academic days', icon: CalendarDays, action: 'guide:day' },
    ] },
  ],

  contacts: {
    storageKey: 'mda-neuro-favorites',
    groups: [
      { id: 'neuro', title: 'Neuroanesthesia', open: true, items: [
        { label: 'Neuro resident phone', phone: '7133015790', note: 'Carry it. Call the attending the day before each case' },
        { label: 'Stephanie Lavelle, rotation coordinator', phone: '2816107096', note: 'Modules, orientation, badge' },
      ] },
    ],
  },

  locations: {
    groups: [
      { id: 'or', title: 'OR', items: [
        { name: 'Your OR', floor: 'Main Building', note: 'Assignment in Epic the evening before' },
        { name: 'OR pharmacy', floor: 'OR', note: 'Remifentanil usually comes from here' },
      ] },
    ],
  },

  documents: {
    storageKey: 'mda-neuro-pinned-docs',
    groups: [
      { id: 'neuro', title: 'Neuro', items: [
        { title: 'Neuro orientation', subtitle: '2026. Blackboard', url: NEURO },
        { title: 'NeuRecipes', subtitle: 'MD Anderson guide to anesthesia for neurosurgical cases, case by case. Blackboard', url: NEURO },
        { title: 'Scalp block: ultrasound-guided review', subtitle: 'Blackboard', url: NEURO },
        { title: 'Scalp block: techniques and application', subtitle: 'Blackboard', url: NEURO },
      ] },
    ],
  },

  guides: {
    setup: {
      title: 'Case Setup',
      intro: 'Almost every case is the same.',
      sections: [
        { title: 'The standard crani', blocks: [{ type: 'rows', rows: [
          { label: 'Anesthetic', value: 'GETA. Propofol and remifentanil TIVA with 0.5 MAC volatile' },
          { label: 'Access', value: 'Two IVs. Arterial line' },
          { label: 'Pumps', value: 'Remifentanil and propofol on syringe pumps' },
          { label: 'Paralytic', value: 'Only without neuromonitoring' },
          { label: 'Pressor', value: 'Phenylephrine on a syringe pump' },
          { label: 'Bed', value: 'Turned 90°, usually induced turned. Ask the attending' },
        ] }] },
        { title: 'Standard concentrations', blocks: [{ type: 'rows', rows: [
          { label: 'Remifentanil', value: '50 mcg/mL' },
          { label: 'Nicardipine', value: '0.5 mg/mL' },
          { label: 'Dexmedetomidine', value: '10 mcg/mL' },
        ] }] },
        { title: 'Drugs', blocks: [{ type: 'bullets', items: [
          'Dexamethasone 10 mg for most cranis. Not pituitary. Ask the surgical team; it usually comes up at the timeout',
          'Ondansetron 4–8 mg for everyone',
          'Cefazolin for most cranis. The surgical team specifies; vancomycin may already be running from preop',
        ] }] },
        { title: 'MDA pharmacy rules', blocks: [{ type: 'bullets', items: [
          { text: 'Ephedrine is a controlled substance here. Do not pull it until you need it. Give at least 1 mL once drawn. Never waste a full syringe', warn: true },
          'Wasting a full vial or syringe of narcotic needs a written explanation. Draw up only what you will give',
          'Remifentanil usually has to come from the OR pharmacy',
        ] }] },
      ],
      sources: [{ title: 'MDA Neuro Anesthesia Orientation', href: NEURO, detail: '2026. Blackboard, sign-in required' }],
    },

    scalp: {
      title: 'Scalp Block',
      sections: [
        { title: 'Mix', blocks: [{ type: 'rows', rows: [
          { label: 'Ropivacaine 0.5%', value: '30 mL' },
          { label: 'Epinephrine', value: '150 mcg', note: '5 mcg/mL, about 1:200,000' },
          { label: 'Syringes', value: 'Three 10 mL' },
          { label: 'Needle', value: '25 or 27 G' },
        ] }, { type: 'note', text: 'Ask the attending whether the patient is a candidate. Technique in the two scalp block reviews under Documents.' }] },
      ],
      sources: [{ title: 'MDA Neuro Anesthesia Orientation', href: NEURO, detail: 'Blackboard, sign-in required' }],
    },

    day: {
      title: 'Your Day',
      sections: [
        { title: 'Before you start', blocks: [{ type: 'bullets', items: [
          'OneConnect modules and training from the coordinator’s email',
        ] }] },
        { title: 'First day', blocks: [{ type: 'steps', items: [
          'Orientation with the coordinator, with a video',
          'MRI access form and modules',
          'Check the badge works. Scrub access or temporary scrubs',
          'Meet Dr Oliver for resources, email and a short OR orientation',
          'Pick up the neuro resident phone',
        ] }] },
        { title: 'Every evening', blocks: [{ type: 'bullets', items: [
          'Pre-chart tomorrow’s case and text or call the attending with your plan',
          'Starting on a Friday: Monday’s case is pre-opped on Friday, not Sunday',
        ] }] },
        { title: 'Every morning', blocks: [{ type: 'bullets', items: [
          'OR assignment in Epic. Set up MSMAIDS, the second IV and arterial line, and the remifentanil and propofol pumps',
        ] }] },
        { title: 'Academic days', blocks: [{ type: 'rows', rows: [
          { label: 'CA-2', value: 'Two days, set by Dr Oliver' },
          { label: 'CA-3', value: 'Tuesdays' },
        ] }] },
      ],
      sources: [{ title: 'MDA Neuro Anesthesia Orientation', href: NEURO, detail: 'Blackboard, sign-in required' }],
    },
  },
};
